import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setShowLangMenu(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'
      } translate-y-0`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3 cursor-pointer" aria-label="Yoshino Village Network ホームページへ">
            <img
              src="https://public.readdy.ai/ai/img_res/dddcdb8a-4f07-441e-8e3f-1ae6a286ee38.png"
              alt="Yoshino Village Network Logo"
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-lg font-bold text-stone-800">Yoshino Village Network</h1>
              <p className="text-xs text-stone-600">{i18n.language === 'ja' ? '挑戦する人と地域が出会う場所' : 'Where challengers meet community'}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer">
              {t('nav.home')}
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer">
              {t('nav.about')}
            </Link>
            <a 
              href="#steps" 
              onClick={(e) => handleSectionClick(e, 'three-steps')}
              className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
            >
              {i18n.language === 'ja' ? '最初のステップ' : 'First Steps'}
            </a>
            <a 
              href="#this-week" 
              onClick={(e) => handleSectionClick(e, 'this-week')}
              className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
            >
              {i18n.language === 'ja' ? '今週の動き' : 'This Week'}
            </a>
            <a 
              href="#people" 
              onClick={(e) => handleSectionClick(e, 'people')}
              className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
            >
              {i18n.language === 'ja' ? 'メンバー' : 'Members'}
            </a>
            <a
              href="https://note.com/yoshinovillage"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
            >
              note
            </a>
            <Link to="/contact" className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer">
              {t('nav.contact')}
            </Link>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
                aria-label="言語を選択"
              >
                <i className="ri-global-line text-xl w-5 h-5 flex items-center justify-center"></i>
                <span className="uppercase text-sm">{i18n.language}</span>
              </button>
              
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                  <button
                    onClick={() => changeLanguage('ja')}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    日本語
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                  >
                    English
                  </button>
                </div>
              )}
            </div>

            {/* Consultation Button */}
            <Link
              to="/consultation"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-full font-bold transition-all shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              {i18n.language === 'ja' ? '相談する' : 'Consult'}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-stone-700 hover:text-amber-600 transition-colors"
            aria-label={isMobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
            aria-expanded={isMobileMenuOpen}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-stone-200 pt-4">
            <div className="flex flex-col gap-6 py-8">
              <Link to="/" className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer">
                {t('nav.home')}
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer">
                {t('nav.about')}
              </Link>
              <a 
                href="#steps" 
                onClick={(e) => handleSectionClick(e, 'three-steps')}
                className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
              >
                {i18n.language === 'ja' ? '最初のステップ' : 'First Steps'}
              </a>
              <a 
                href="#this-week" 
                onClick={(e) => handleSectionClick(e, 'this-week')}
                className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
              >
                {i18n.language === 'ja' ? '今週の動き' : 'This Week'}
              </a>
              <a 
                href="#people" 
                onClick={(e) => handleSectionClick(e, 'people')}
                className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
              >
                {i18n.language === 'ja' ? 'メンバー' : 'Members'}
              </a>
              <a
                href="https://note.com/yoshinovillage"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer"
              >
                note
              </a>
              <Link to="/contact" className="text-gray-700 hover:text-orange-600 font-medium transition-colors cursor-pointer">
                {t('nav.contact')}
              </Link>

              {/* Mobile Consultation Button */}
              <Link
                to="/consultation"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-bold transition-all shadow-md text-center cursor-pointer whitespace-nowrap"
              >
                {i18n.language === 'ja' ? '相談する' : 'Consult'}
              </Link>

              {/* Mobile Language Switcher */}
              <div className="flex gap-4 pt-4 border-t border-stone-200">
                <button
                  onClick={() => changeLanguage('ja')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    i18n.language === 'ja'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  日本語
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    i18n.language === 'en'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  English
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
